import { Divider, Grid, Loader } from "@aws-amplify/ui-react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";

import {
  createServiceOrderPhoto,
  deleteServiceOrderPhoto,
} from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import { serviceOrderPhotosByServiceOrderID } from "graphql/queries";
import Gallery from "components/utils/Gallery";
import LoaderFullScreen from "components/utils/LoaderFullScreen";

const ServiceOrderPhotos = ({ serviceOrderID }) => {
  const [canUpload, canDelete] = usePermissions([
    "service-order:upload-photo",
    "service-order:delete-photo",
  ]);
  const [loading, setLoading] = useState(false);
  const [serviceOrderPhotos, setServiceOrderPhotos] = useState([]);
  const [serviceOrderPhotosLoading, setServiceOrderPhotosLoading] =
    useState(false);
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    serviceOrderPhotoId: null,
  });

  async function onCreate(fileObj, tag) {
    try {
      setLoading(true);

      let fileSrc = "";
      let filePreview = "";

      if (typeof fileObj === "string") {
        fileSrc = `photo_${Date.now()}.jpeg`;

        await Storage.put(`service-order-photos/${fileSrc}`, fileObj, {
          contentType: "image/jpeg",
          level: "public",
        });

        filePreview = fileObj;
      }
      else if (fileObj) {
        fileSrc = `${fileObj.name
          .split(".")
          .slice(0, -1)
          .join(".")}_${Date.now()}.${fileObj.name.split(".").slice(-1)[0]}`;

        await Storage.put(`service-order-photos/${fileSrc}`, fileObj, {
          contentType: fileObj.type,
          level: "public",
        });

        const reader = new FileReader();
        reader.onloadend = () => {
          filePreview = reader.result;
        };
        reader.readAsDataURL(fileObj);
      }

      const newPhoto = (
        await API.graphql({
          query: createServiceOrderPhoto.replaceAll("__typename", ""),
          variables: {
            input: {
              serviceOrderID,
              photo: fileSrc,
              tag,
            },
          },
        })
      ).data.createServiceOrderPhoto;

      setServiceOrderPhotos((prev) => [
        ...(prev ?? []),
        { ...newPhoto, photo: filePreview },
      ]);

      toast.success(strings.successAdded);
    } catch (err) {
      console.log("Error Upload Photo", err);
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  }

  async function fetchServiceOrderPhotos(id) {
    try {
      setServiceOrderPhotosLoading(true);
      const serviceOrderPhotosData = await API.graphql(
        graphqlOperation(serviceOrderPhotosByServiceOrderID, {
          serviceOrderID: id,
        })
      );
      const serviceOrderPhotosItems =
        serviceOrderPhotosData.data.serviceOrderPhotosByServiceOrderID.items;

      const newList = await Promise.all(
        serviceOrderPhotosItems?.map(async (serviceOrderPhoto) => {
          const src = await Storage.get(
            `service-order-photos/${serviceOrderPhoto.photo}`,
            {
              level: "public",
            }
          );
          return {
            ...serviceOrderPhoto,
            photo: src,
          };
        }) || []
      );

      setServiceOrderPhotos(newList);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    } finally {
      setServiceOrderPhotosLoading(false);
    }
  }

  async function serviceOrderPhotoDelete(id) {
    try {
      setLoading(true);

      const result = await API.graphql(
        graphqlOperation(deleteServiceOrderPhoto, { input: { id } })
      );
      toast.success(strings.successDeleted);
      const { id: deletedId, photo: deletedSrc } =
        result.data.deleteServiceOrderPhoto;
      setServiceOrderPhotos(
        serviceOrderPhotos.filter((item) => item.id !== deletedId)
      );
      setModalDetails({
        visible: false,
        serviceOrderPhotoId: null,
      });

      if (deletedSrc)
        await Storage.remove(`service-order-photos/${deletedSrc}`, {
          level: "public",
        });
    } catch (err) {
      toast.error(strings.responses[err]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchServiceOrderPhotos(serviceOrderID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceOrderID]);

  return (
    <>
      <div>
        <div className="p-3">
          {serviceOrderPhotosLoading ? (
            <Loader />
          ) : (
            <Grid rowGap="15px" columnGap="15px">
              {Object.entries(strings.gallery.tags).map((gal, index) => (
                <>
                  {index > 0 && <Divider className="mt-3 mb-2" />}
                  <Gallery
                    title={gal[1]}
                    images={
                      serviceOrderPhotos
                        ?.filter((photo) => photo.tag === gal[0])
                        .sort(
                          (a, b) =>
                            new Date(a.createdAt) - new Date(b.createdAt)
                        )
                        .map((photoModel) => ({
                          id: photoModel.id,
                          src: photoModel.photo,
                        })) ?? []
                    }
                    onCreate={canUpload && ((photo) => onCreate(photo, gal[0]))}
                    onDelete={canDelete && serviceOrderPhotoDelete}
                  />
                </>
              ))}
            </Grid>
          )}
        </div>
        <ConfirmModal
          visible={modalDetails.visible}
          body={strings.formatString(strings.modal.confirmDeleteQuestion)}
          onCancel={() =>
            setModalDetails({
              visible: false,
              serviceOrderPhotoId: null,
            })
          }
          onConfirm={() =>
            serviceOrderPhotoDelete(modalDetails.serviceOrderPhotoId)
          }
        />
      </div>
      {loading && <LoaderFullScreen />}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Fotos de la Orden de Servicio",
    gallery: {
      tags: {
        beforeStart: "Antes de iniciar",
        inExecution: "Durante la ejecución",
        atEnd: "Al finalizar",
      },
    },
    successAdded: "Foto agregada con éxito",
    successDeleted: "Foto eliminada con éxito",
    responses: {
      errorFetch: "Error al consultar fotos de la orden",
      unexpectedError: "Ha ocurrido un error inesperado",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar esta foto?",
    },
  },
});

export default ServiceOrderPhotos;
